import {
  HE_CLASSES,
  HE_GRADE_COMPONENTS,
  HE_ACTIVITIES,
  HE_CLASS_STUDENTS,
  STUDENTS,
  SCHOOLS,
  GRADE_SUBMISSIONS,
} from "../../constants/collections";
import {
  transmute,
  calculateFinalGradePercent,
  computeGCWeightedScore,
  hedRemarks,
  hedDefaultGradeComponents,
  hedPeriods,
  HED_SPECIAL_GRADES,
} from "../../constants/hedGrades";
import sum from "lodash/sum";
import map from "lodash/map";
import values from "lodash/values";

// ---------------------------------------------------------------------------
// Action Types
// ---------------------------------------------------------------------------
export const SAVE_HED_ACTIVITY_START = "SAVE_HED_ACTIVITY_START";
export const SAVE_HED_ACTIVITY_SUCCESS = "SAVE_HED_ACTIVITY_SUCCESS";
export const SAVE_HED_ACTIVITY_FAIL = "SAVE_HED_ACTIVITY_FAIL";

export const SET_HED_SPECIAL_GRADE_START = "SET_HED_SPECIAL_GRADE_START";
export const SET_HED_SPECIAL_GRADE_SUCCESS = "SET_HED_SPECIAL_GRADE_SUCCESS";
export const SET_HED_SPECIAL_GRADE_FAIL = "SET_HED_SPECIAL_GRADE_FAIL";

export const CLEAR_HED_SPECIAL_GRADE_START = "CLEAR_HED_SPECIAL_GRADE_START";
export const CLEAR_HED_SPECIAL_GRADE_SUCCESS =
  "CLEAR_HED_SPECIAL_GRADE_SUCCESS";
export const CLEAR_HED_SPECIAL_GRADE_FAIL = "CLEAR_HED_SPECIAL_GRADE_FAIL";

export const LOCK_HED_GRADES_START = "LOCK_HED_GRADES_START";
export const LOCK_HED_GRADES_SUCCESS = "LOCK_HED_GRADES_SUCCESS";
export const LOCK_HED_GRADES_FAIL = "LOCK_HED_GRADES_FAIL";

export const UNLOCK_HED_GRADES_START = "UNLOCK_HED_GRADES_START";
export const UNLOCK_HED_GRADES_SUCCESS = "UNLOCK_HED_GRADES_SUCCESS";
export const UNLOCK_HED_GRADES_FAIL = "UNLOCK_HED_GRADES_FAIL";

export const REVIEW_GRADE_SUBMISSION_START = "REVIEW_GRADE_SUBMISSION_START";
export const REVIEW_GRADE_SUBMISSION_SUCCESS =
  "REVIEW_GRADE_SUBMISSION_SUCCESS";
export const REVIEW_GRADE_SUBMISSION_FAIL = "REVIEW_GRADE_SUBMISSION_FAIL";

export const RESOLVE_HED_TBA_START = "RESOLVE_HED_TBA_START";
export const RESOLVE_HED_TBA_SUCCESS = "RESOLVE_HED_TBA_SUCCESS";
export const RESOLVE_HED_TBA_FAIL = "RESOLVE_HED_TBA_FAIL";

export const INIT_HED_GRADE_COMPONENTS_START =
  "INIT_HED_GRADE_COMPONENTS_START";
export const INIT_HED_GRADE_COMPONENTS_SUCCESS =
  "INIT_HED_GRADE_COMPONENTS_SUCCESS";
export const INIT_HED_GRADE_COMPONENTS_FAIL = "INIT_HED_GRADE_COMPONENTS_FAIL";

export const SAVE_HED_GRADE_COMPONENTS = "SAVE_HED_GRADE_COMPONENTS";

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Fetch all students in a class plus their current grade doc.
 */
const _fetchClassStudents = async (db, classId) => {
  const snap = await db
    .collection(HE_CLASSES)
    .doc(classId)
    .collection(HE_CLASS_STUDENTS)
    .get();
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

/**
 * Aggregate activity scores for a given GC+period across all he_activities.
 * Returns { [studentId]: { rawTotal, hps } }
 */
const _aggregateGCScores = async (db, classId, gcId, period) => {
  // gcId + period equality query only — no composite index required.
  // The deleted filter is applied in-memory to avoid a Firestore composite index requirement.
  const activitiesSnap = await db
    .collection(HE_CLASSES)
    .doc(classId)
    .collection(HE_ACTIVITIES)
    .where("gcId", "==", gcId)
    .where("period", "==", period)
    .get();

  const studentTotals = {}; // { studentId: { raw: number, hps: number } }
  activitiesSnap.docs.forEach((doc) => {
    const act = doc.data();
    // skip soft-deleted activities
    if (act.deleted) return;
    const hps = act.highestPossibleScore || 0;
    Object.entries(act.scores || {}).forEach(([studentId, score]) => {
      if (!studentTotals[studentId]) {
        studentTotals[studentId] = { raw: 0, hps: 0 };
      }
      studentTotals[studentId].raw += score || 0;
      studentTotals[studentId].hps += hps;
    });
  });

  // Sum total HPS across non-deleted activities only
  const totalHPS = sum(
    activitiesSnap.docs
      .filter((d) => !d.data().deleted)
      .map((d) => d.data().highestPossibleScore || 0),
  );

  return { studentTotals, totalHPS };
};

/**
 * Read all GC totals for a student in a period and recompute the period grade.
 * periodGrade = sum of all GC weighted scores (WS)
 */
const _recalcPeriodGrade = (gcTotalsMap) => {
  const wsValues = map(values(gcTotalsMap), "ws");
  return Math.round(sum(wsValues) * 100) / 100;
};

// ---------------------------------------------------------------------------
// Auto-initialize grade components (3 periods × 3 types = 9 docs)
// ---------------------------------------------------------------------------
export const initHedGradeComponents =
  (classId) =>
  async (dispatch, getState, { getFirestore }) => {
    dispatch({ type: INIT_HED_GRADE_COMPONENTS_START });
    const db = getFirestore();
    try {
      const gcColRef = db
        .collection(HE_CLASSES)
        .doc(classId)
        .collection(HE_GRADE_COMPONENTS);

      // Check if already initialized
      const existing = await gcColRef.limit(1).get();
      if (!existing.empty) {
        dispatch({ type: INIT_HED_GRADE_COMPONENTS_SUCCESS });
        return;
      }

      const batch = db.batch();
      hedPeriods.forEach(({ value: period }) => {
        hedDefaultGradeComponents.forEach(({ type, name, percent }) => {
          const docRef = gcColRef.doc();
          batch.set(docRef, {
            period,
            type,
            name,
            percent,
            deleted: false,
            createdOn: db.FieldValue.serverTimestamp(),
          });
        });
      });
      await batch.commit();
      dispatch({ type: INIT_HED_GRADE_COMPONENTS_SUCCESS });
    } catch (error) {
      console.error("initHedGradeComponents error", error);
      dispatch({ type: INIT_HED_GRADE_COMPONENTS_FAIL, error: error.message });
    }
  };

// ---------------------------------------------------------------------------
// Save / edit grade components for one period (add, rename, delete, reweight)
// ---------------------------------------------------------------------------
/**
 * Upsert all GCs in `gradeComponents` for the given classId + period,
 * and soft-delete any GC ids listed in `toDelete`.
 *
 * @param {string} classId
 * @param {string} period — "prelim" | "midterm" | "finals"
 * @param {Array<{id?: string, name: string, percent: number, type?: string}>} gradeComponents
 * @param {string[]} toDelete — ids of GC docs to soft-delete
 */
export const saveHedGradeComponents =
  (classId, period, gradeComponents, toDelete = []) =>
  async (dispatch, getState, { getFirestore }) => {
    const db = getFirestore();
    const gcColRef = db
      .collection(HE_CLASSES)
      .doc(classId)
      .collection(HE_GRADE_COMPONENTS);

    const batch = db.batch();

    gradeComponents.forEach((gc) => {
      const docRef = gc.id ? gcColRef.doc(gc.id) : gcColRef.doc();
      const data = gc.id
        ? { name: gc.name, percent: gc.percent }
        : {
            name: gc.name,
            percent: gc.percent,
            type: gc.type || "custom",
            period,
            deleted: false,
            createdOn: db.FieldValue.serverTimestamp(),
          };
      batch.set(docRef, data, { merge: true });
    });

    toDelete.forEach((gcId) => {
      batch.update(gcColRef.doc(gcId), {
        deleted: true,
        deletedOn: db.FieldValue.serverTimestamp(),
      });
    });

    await batch.commit();
    dispatch({ type: SAVE_HED_GRADE_COMPONENTS });

    // ── Step A: recalculate WS for active GCs with the new percent weights ────
    // This corrects stale WS values whenever percentages change.
    const activeGCs = gradeComponents.filter((gc) => gc.id && !gc.deleted);
    for (const gc of activeGCs) {
      try {
        await dispatch(
          _recalculateHedGCTotal({ classId, gcId: gc.id, period }),
        );
      } catch (e) {
        console.warn(
          "[HED] saveHedGradeComponents: cascade failed for GC",
          gc.id,
          e,
        );
      }
    }

    // ── Step B: remove deleted GC totals from every student ──────────────────
    // Without this, the deleted GC's old WS keeps contributing to period grade.
    if (toDelete.length > 0) {
      const students = await _fetchClassStudents(db, classId);
      if (students.length > 0) {
        const studentsRef = db
          .collection(HE_CLASSES)
          .doc(classId)
          .collection(HE_CLASS_STUDENTS);

        // Remove the deleted GC's key from gcTotals for all students
        const deleteBatch = db.batch();
        students.forEach((student) => {
          toDelete.forEach((deletedGcId) => {
            deleteBatch.set(
              studentsRef.doc(student.id),
              {
                grades: {
                  [period]: {
                    gcTotals: {
                      [deletedGcId]: db.FieldValue.delete(),
                    },
                  },
                },
              },
              { merge: true },
            );
          });
        });
        await deleteBatch.commit();

        // Re-run period grade cascade with the deleted key now gone
        const studentIds = students.map((s) => s.id);
        try {
          await dispatch(
            _recalculateHedPeriodGrade({ classId, period, studentIds }),
          );
        } catch (e) {
          console.warn(
            "[HED] saveHedGradeComponents: period recalc after delete failed",
            e,
          );
        }
      }
    }
  };

// ---------------------------------------------------------------------------
// Save / update a grade activity and cascade grade recalculation
// ---------------------------------------------------------------------------
/**
 * Save (create or update) an HED activity and trigger the full grade
 * recalculation cascade for the GC → period → final grades.
 *
 * @param {object} params
 * @param {string} params.classId
 * @param {string|null} params.activityId — null to create new
 * @param {string} params.gcId
 * @param {string} params.period — "prelim" | "midterm" | "finals"
 * @param {string} params.name
 * @param {number} params.highestPossibleScore
 * @param {object} params.scores — { [studentId]: score }
 */
export const saveHedActivity =
  ({
    classId,
    activityId,
    gcId,
    period,
    name,
    highestPossibleScore,
    scores,
    date, // Date object — when the activity was conducted
    description, // optional string
  }) =>
  async (dispatch, getState, { getFirestore }) => {
    dispatch({ type: SAVE_HED_ACTIVITY_START });
    const db = getFirestore();
    try {
      const actColRef = db
        .collection(HE_CLASSES)
        .doc(classId)
        .collection(HE_ACTIVITIES);

      // Firestore accepts plain JS Date objects natively — no need for Timestamp.fromDate()
      const activityDate = date instanceof Date ? date : null;
      const formattedDate =
        date instanceof Date
          ? date.toLocaleDateString("en-PH", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })
          : null;

      // Sanitize scores: ensure plain Number values (avoids Formik proxy / string issues)
      const cleanScores = Object.fromEntries(
        Object.entries(scores || {}).map(([k, v]) => [k, Number(v) || 0]),
      );

      if (activityId) {
        // Use set+merge instead of update — semantically identical for existing docs
        // but avoids a Firestore emulator bug where .update() on subcollection docs
        // silently hangs after extended emulator uptime.
        await actColRef.doc(activityId).set(
          {
            name,
            highestPossibleScore,
            scores: cleanScores,
            gcId,
            period,
            ...(activityDate !== null && { date: activityDate, formattedDate }),
            ...(description !== undefined && { description }),
            updatedAt: db.FieldValue.serverTimestamp(),
          },
          { merge: true },
        );
      } else {
        await actColRef.add({
          gcId,
          period,
          name,
          highestPossibleScore,
          scores: cleanScores,
          date: activityDate,
          formattedDate,
          description: description || "",
          deleted: false,
          createdOn: db.FieldValue.serverTimestamp(),
        });
      }

      // Cascade: recalculate GC total → period grade → final grade
      await dispatch(_recalculateHedGCTotal({ classId, gcId, period }));

      dispatch({ type: SAVE_HED_ACTIVITY_SUCCESS });
    } catch (error) {
      dispatch({ type: SAVE_HED_ACTIVITY_FAIL, error: error.message });
      throw error;
    }
  };

/**
 * Soft-delete an HED activity and recalculate.
 */
export const deleteHedActivity =
  ({ classId, activityId, gcId, period }) =>
  async (dispatch, getState, { getFirestore }) => {
    dispatch({ type: SAVE_HED_ACTIVITY_START });
    const db = getFirestore();
    try {
      await db
        .collection(HE_CLASSES)
        .doc(classId)
        .collection(HE_ACTIVITIES)
        .doc(activityId)
        .update({ deleted: true, deletedAt: db.FieldValue.serverTimestamp() });

      await dispatch(_recalculateHedGCTotal({ classId, gcId, period }));
      dispatch({ type: SAVE_HED_ACTIVITY_SUCCESS });
    } catch (error) {
      console.error("deleteHedActivity error", error);
      dispatch({ type: SAVE_HED_ACTIVITY_FAIL, error: error.message });
      throw error;
    }
  };

// ---------------------------------------------------------------------------
// Internal cascade — NOT exported (no need for components to call these directly)
// ---------------------------------------------------------------------------

/**
 * Step 1 of cascade: recompute GC total for all students → triggers period grade.
 */
const _recalculateHedGCTotal =
  ({ classId, gcId, period }) =>
  async (dispatch, getState, { getFirestore }) => {
    const db = getFirestore();

    // Get GC metadata (percent weight)
    const gcSnap = await db
      .collection(HE_CLASSES)
      .doc(classId)
      .collection(HE_GRADE_COMPONENTS)
      .doc(gcId)
      .get();
    if (!gcSnap.exists) {
      console.warn("[HED] 3️⃣ GC doc not found! Abort.", gcId);
      return;
    }
    const { percent } = gcSnap.data();
    // Aggregate activity scores for this GC+period
    const { studentTotals, totalHPS } = await _aggregateGCScores(
      db,
      classId,
      gcId,
      period,
    );

    // Fetch class student list
    const students = await _fetchClassStudents(db, classId);

    // Batch update each student's gcTotals for this GC+period
    const batch = db.batch();
    const studentsRef = db
      .collection(HE_CLASSES)
      .doc(classId)
      .collection(HE_CLASS_STUDENTS);

    const studentIds = students.map((s) => s.id);
    studentIds.forEach((studentId) => {
      const raw = studentTotals[studentId]?.raw || 0;
      const { ps, ws } = computeGCWeightedScore(raw, totalHPS, percent);
      batch.set(
        studentsRef.doc(studentId),
        {
          grades: {
            [period]: {
              gcTotals: {
                [gcId]: { rawTotal: raw, hps: totalHPS, ps, ws },
              },
            },
          },
        },
        { merge: true },
      );
    });

    await batch.commit();
    // Step 2: recalculate period grade for all students
    await dispatch(_recalculateHedPeriodGrade({ classId, period, studentIds }));
  };

/**
 * Step 2 of cascade: sum all GC WS values → period grade.
 */
const _recalculateHedPeriodGrade =
  ({ classId, period, studentIds }) =>
  async (dispatch, getState, { getFirestore }) => {
    const db = getFirestore();
    const studentsRef = db
      .collection(HE_CLASSES)
      .doc(classId)
      .collection(HE_CLASS_STUDENTS);

    const batch = db.batch();

    await Promise.all(
      studentIds.map(async (studentId) => {
        const studentSnap = await studentsRef.doc(studentId).get();
        const studentData = studentSnap.data() || {};
        const gcTotals = studentData?.grades?.[period]?.gcTotals || {};

        // Skip if student has a special grade override
        const currentRemarks = studentData?.grades?.remarks;
        if (HED_SPECIAL_GRADES.includes(currentRemarks)) return;

        const periodGrade = _recalcPeriodGrade(gcTotals);

        batch.set(
          studentsRef.doc(studentId),
          { grades: { [period]: { periodGrade } } },
          { merge: true },
        );
      }),
    );
    await batch.commit();

    // Step 3: try to compute final grade (only succeeds if all 3 periods done)
    await dispatch(_recalculateHedFinalGrade({ classId, studentIds }));
  };

/**
 * Step 3 of cascade: compute final % → transmute → write remarks; mirror to enrollment.
 */
const _recalculateHedFinalGrade =
  ({ classId, studentIds }) =>
  async (dispatch, getState, { getFirestore }) => {
    const db = getFirestore();
    const studentsRef = db
      .collection(HE_CLASSES)
      .doc(classId)
      .collection(HE_CLASS_STUDENTS);

    // Get class doc for subjectId & schoolYear
    const classSnap = await db.collection(HE_CLASSES).doc(classId).get();
    if (!classSnap.exists) return;
    const { subjectId, schoolYear } = classSnap.data();

    const batch = db.batch();
    const enrollmentUpdates = []; // deferred outside the batch (different collection)

    await Promise.all(
      studentIds.map(async (studentId) => {
        const studentSnap = await studentsRef.doc(studentId).get();
        const studentData = studentSnap.data() || {};
        const grades = studentData.grades || {};

        // Skip if student has a manual special grade override
        if (HED_SPECIAL_GRADES.includes(grades.remarks)) return;

        const prelim = grades?.prelim?.periodGrade;
        const midterm = grades?.midterm?.periodGrade;
        const finals = grades?.finals?.periodGrade;

        // Only compute if all 3 periods are graded
        if (
          typeof prelim !== "number" ||
          typeof midterm !== "number" ||
          typeof finals !== "number"
        )
          return;

        const finalGradePercent = calculateFinalGradePercent({
          prelim,
          midterm,
          finals,
        });
        const finalGradePoints = transmute(finalGradePercent);
        const remarks = hedRemarks(finalGradePoints);

        batch.set(
          studentsRef.doc(studentId),
          { grades: { finalGradePercent, finalGradePoints, remarks } },
          { merge: true },
        );

        // Mirror to student enrollment doc
        enrollmentUpdates.push({
          studentId,
          subjectId,
          finalGradePoints,
          remarks,
        });
      }),
    );
    await batch.commit();

    // ── Denormalized gradingStats ─────────────────────────────────────────────
    // Compute live counts from all students (not just those in this batch) so
    // the stat on he_classes is always accurate for the Subject Classes table.
    try {
      const allStudentsSnap = await studentsRef.get();
      const total = allStudentsSnap.size;
      let graded = 0;
      allStudentsSnap.forEach((doc) => {
        const fp = doc.data()?.grades?.finalGradePoints;
        if (typeof fp === "number" || HED_SPECIAL_GRADES.includes(fp)) graded++;
      });
      await db
        .collection(HE_CLASSES)
        .doc(classId)
        .set(
          { gradingStats: { total, graded, pending: total - graded } },
          { merge: true },
        );
    } catch (e) {
      // Non-fatal — stats are cosmetic
      console.warn("[HED] gradingStats update failed", e);
    }

    // Mirror final grade to enrollment docs (outside batch — different collection path)
    await Promise.all(
      enrollmentUpdates.map(
        async ({ studentId, subjectId: sid, finalGradePoints, remarks }) => {
          try {
            const enrollRef = db
              .collection(STUDENTS)
              .doc(studentId)
              .collection("enrollments")
              .doc(schoolYear);
            const enrollSnap = await enrollRef.get();
            if (!enrollSnap.exists) return;
            const data = enrollSnap.data();
            const updatedSubjects = (data.subjects || []).map((s) =>
              s.id === sid ? { ...s, finalGradePoints, remarks } : s,
            );
            await enrollRef.update({ subjects: updatedSubjects });
          } catch (e) {
            console.error("Mirror grade to enrollment failed", studentId, e);
          }
        },
      ),
    );
  };

// ---------------------------------------------------------------------------
// Set a special grade (INC / DRP) — overrides computed grade
// ---------------------------------------------------------------------------
export const setHedSpecialGrade =
  ({
    classId,
    studentId,
    grade, // "INC" | "DRP"
  }) =>
  async (dispatch, getState, { getFirestore }) => {
    dispatch({ type: SET_HED_SPECIAL_GRADE_START });
    const db = getFirestore();
    try {
      const classSnap = await db.collection(HE_CLASSES).doc(classId).get();
      const { subjectId, schoolYear } = classSnap.data() || {};

      // Update student grade doc
      await db
        .collection(HE_CLASSES)
        .doc(classId)
        .collection(HE_CLASS_STUDENTS)
        .doc(studentId)
        .set(
          { grades: { finalGradePoints: grade, remarks: grade } },
          { merge: true },
        );

      // Mirror to enrollment doc
      if (subjectId && schoolYear) {
        const enrollRef = db
          .collection(STUDENTS)
          .doc(studentId)
          .collection("enrollments")
          .doc(schoolYear);
        const enrollSnap = await enrollRef.get();
        if (enrollSnap.exists) {
          const data = enrollSnap.data();
          const updatedSubjects = (data.subjects || []).map((s) =>
            s.id === subjectId
              ? { ...s, finalGradePoints: grade, remarks: grade }
              : s,
          );
          await enrollRef.update({ subjects: updatedSubjects });
        }
      }

      dispatch({ type: SET_HED_SPECIAL_GRADE_SUCCESS });
    } catch (error) {
      console.error("setHedSpecialGrade error", error);
      dispatch({ type: SET_HED_SPECIAL_GRADE_FAIL, error: error.message });
      throw error;
    }
  };

// ---------------------------------------------------------------------------
// Clear a special grade override — restores the computed grade
// ---------------------------------------------------------------------------
export const clearHedSpecialGrade =
  ({ classId, studentId }) =>
  async (dispatch, getState, { getFirestore }) => {
    dispatch({ type: CLEAR_HED_SPECIAL_GRADE_START });
    const db = getFirestore();
    try {
      const studentRef = db
        .collection(HE_CLASSES)
        .doc(classId)
        .collection(HE_CLASS_STUDENTS)
        .doc(studentId);

      // 1. Remove the special grade override fields from the student doc
      await studentRef.set(
        {
          grades: {
            finalGradePoints: db.FieldValue.delete(),
            remarks: db.FieldValue.delete(),
          },
        },
        { merge: true },
      );

      // 2. Re-run the final grade cascade from the stored period grades.
      //    This recomputes finalGradePoints + remarks and writes them back.
      //    Re-uses the same internal helper used by the normal grading cascade.
      const studentSnap = await studentRef.get();
      const studentData = studentSnap.data() || {};
      const classSnap = await db.collection(HE_CLASSES).doc(classId).get();
      const { periodWeights, subjectId, schoolYear } = classSnap.data() || {};

      const periodGrades = {};
      hedPeriods.forEach((p) => {
        const pg = studentData.grades?.[p.value]?.periodGrade;
        if (typeof pg === "number") periodGrades[p.value] = pg;
      });

      if (Object.keys(periodGrades).length === hedPeriods.length) {
        const finalPct = calculateFinalGradePercent(
          periodGrades,
          periodWeights,
        );
        const finalPoints = transmute(finalPct);
        const remarks = hedRemarks(finalPoints);

        await studentRef.set(
          {
            grades: {
              finalGradePercent: finalPct,
              finalGradePoints: finalPoints,
              remarks,
            },
          },
          { merge: true },
        );

        // Mirror revert to enrollment doc
        if (subjectId && schoolYear) {
          try {
            const enrollRef = db
              .collection(STUDENTS)
              .doc(studentId)
              .collection("enrollments")
              .doc(schoolYear);
            const enrollSnap = await enrollRef.get();
            if (enrollSnap.exists) {
              const data = enrollSnap.data();
              const updatedSubjects = (data.subjects || []).map((s) =>
                s.id === subjectId
                  ? { ...s, finalGradePoints: finalPoints, remarks }
                  : s,
              );
              await enrollRef.update({ subjects: updatedSubjects });
            }
          } catch (e) {
            console.warn("clearHedSpecialGrade: enrollment mirror failed", e);
          }
        }
      }

      dispatch({ type: CLEAR_HED_SPECIAL_GRADE_SUCCESS });
    } catch (error) {
      console.error("clearHedSpecialGrade error", error);
      dispatch({ type: CLEAR_HED_SPECIAL_GRADE_FAIL, error: error.message });
      throw error;
    }
  };

// ---------------------------------------------------------------------------
// Lock grades — marks he_classes as final; disables all grade editing
// Also writes a grade_submissions snapshot for the Registrar review flow.
// ---------------------------------------------------------------------------
export const lockHedGrades =
  ({
    classId,
    lockedBy, // userId of the instructor
    // Extra class metadata for the submission snapshot
    schoolId,
    classSnapshot = {}, // { subjectCode, subjectTitle, instructorId, instructorName, schoolYear, term, programCode, yearLevel, studentCount }
  }) =>
  async (dispatch, getState, { getFirestore }) => {
    dispatch({ type: LOCK_HED_GRADES_START });
    const db = getFirestore();
    try {
      const now = db.FieldValue.serverTimestamp();
      const batch = db.batch();

      // 1. Lock the class
      batch.set(
        db.collection(HE_CLASSES).doc(classId),
        { gradesLocked: true, lockedAt: now, lockedBy },
        { merge: true },
      );

      // 2. Write submission snapshot (only if schoolId provided)
      if (schoolId) {
        batch.set(
          db
            .collection(SCHOOLS)
            .doc(schoolId)
            .collection(GRADE_SUBMISSIONS)
            .doc(classId),
          {
            classId,
            schoolId,
            ...classSnapshot,
            submittedBy: lockedBy,
            submittedAt: now,
            status: "pending",
            // Clear any previous review fields on re-lock
            reviewedAt: null,
            reviewedBy: null,
          },
          { merge: false }, // overwrite on re-lock
        );
      }

      await batch.commit();
      dispatch({ type: LOCK_HED_GRADES_SUCCESS });
    } catch (error) {
      console.error("lockHedGrades error", error);
      dispatch({ type: LOCK_HED_GRADES_FAIL, error: error.message });
      throw error;
    }
  };

// ---------------------------------------------------------------------------
// Unlock grades — Admin / Academic Coordinator / Registrar only
// ---------------------------------------------------------------------------
export const unlockHedGrades =
  ({
    classId,
    unlockedBy, // userId of the admin
  }) =>
  async (dispatch, getState, { getFirestore }) => {
    dispatch({ type: UNLOCK_HED_GRADES_START });
    const db = getFirestore();
    try {
      await db.collection(HE_CLASSES).doc(classId).set(
        {
          gradesLocked: false,
          unlockedAt: db.FieldValue.serverTimestamp(),
          unlockedBy,
          // Preserve lockedAt / lockedBy for audit trail
        },
        { merge: true },
      );
      dispatch({ type: UNLOCK_HED_GRADES_SUCCESS });
    } catch (error) {
      console.error("unlockHedGrades error", error);
      dispatch({ type: UNLOCK_HED_GRADES_FAIL, error: error.message });
      throw error;
    }
  };

// ---------------------------------------------------------------------------
// Review grade submission — Registrar accept or return for revision
// ---------------------------------------------------------------------------
export const reviewGradeSubmission =
  ({
    classId,
    schoolId,
    action, // "accepted" | "returned"
    reviewedBy, // userId of registrar
  }) =>
  async (dispatch, getState, { getFirestore }) => {
    dispatch({ type: REVIEW_GRADE_SUBMISSION_START });
    const db = getFirestore();
    try {
      const now = db.FieldValue.serverTimestamp();
      const batch = db.batch();

      // Update submission status
      batch.set(
        db
          .collection(SCHOOLS)
          .doc(schoolId)
          .collection(GRADE_SUBMISSIONS)
          .doc(classId),
        { status: action, reviewedAt: now, reviewedBy },
        { merge: true },
      );

      // If returning for revision, unlock the class so instructor can edit
      if (action === "returned") {
        batch.set(
          db.collection(HE_CLASSES).doc(classId),
          { gradesLocked: false, unlockedAt: now, unlockedBy: reviewedBy },
          { merge: true },
        );
      }

      await batch.commit();
      dispatch({ type: REVIEW_GRADE_SUBMISSION_SUCCESS });
    } catch (error) {
      console.error("reviewGradeSubmission error", error);
      dispatch({ type: REVIEW_GRADE_SUBMISSION_FAIL, error: error.message });
      throw error;
    }
  };

// ---------------------------------------------------------------------------
// resolveHedTba — assign a real class to a student's TBA subject
// ---------------------------------------------------------------------------
/**
 * @param {object} params
 * @param {string} params.studentId        - student document ID
 * @param {number} params.subjectIdx       - index in enrollment.subjects array
 * @param {string} params.effectiveSy      - e.g. "20252026_sem1"
 * @param {object} params.classData        - { classId, classSchedule, classTeacher, classRoom }
 */
export const resolveHedTba =
  ({ studentId, subjectIdx, effectiveSy, classData }) =>
  async (dispatch, _getState, { getFirestore }) => {
    const db = getFirestore();
    dispatch({ type: RESOLVE_HED_TBA_START });
    try {
      const enrollmentRef = db
        .collection(STUDENTS)
        .doc(studentId)
        .collection("enrollments")
        .doc(effectiveSy);

      // 1. Read current subjects array
      const enrollmentSnap = await enrollmentRef.get();
      if (!enrollmentSnap.exists) {
        throw new Error("Enrollment document not found.");
      }
      const subjects = enrollmentSnap.data().subjects || [];

      // 2. Clone and replace the TBA entry at subjectIdx
      const updatedSubjects = subjects.map((s, idx) => {
        if (idx !== subjectIdx) return s;
        return {
          ...s,
          classId: classData.classId,
          classSchedule: classData.classSchedule || "",
          classTeacher: classData.classTeacher || "",
          classRoom: classData.classRoom || "",
        };
      });

      // 3. Batch write
      const batch = db.batch();

      // 3a. Update the enrollment subjects array
      batch.update(enrollmentRef, { subjects: updatedSubjects });

      // 3b. Add student to he_classes/{classId}/students roster
      const studentInClassRef = db
        .collection(HE_CLASSES)
        .doc(classData.classId)
        .collection(HE_CLASS_STUDENTS)
        .doc(studentId);

      //3b1.1 Fetch the actual student name and lrn form students collection
      const studentSnap = await db
        .collection(STUDENTS)
        .doc(studentId)
        .get()
      
      if (!studentSnap.exists) {
        throw new Error("Student document not found.");
      }

      const studentName = {
        extName: studentSnap.data().extName || "",
        firstName: studentSnap.data().firstName,
        lastName: studentSnap.data().lastName,
        middleName: studentSnap.data().middleName,
      }

      const lrn = studentSnap.data().lrn

      batch.set(
        studentInClassRef,
        {
          name: studentName || {},
          lrn: lrn || "",
          grades: {},
        },
        { merge: true },
      );

      // 3c. Increment class studentCount
      const classRef = db.collection(HE_CLASSES).doc(classData.classId);
      batch.update(classRef, {
        studentCount: db.FieldValue.increment(1),
      });

      await batch.commit();
      dispatch({ type: RESOLVE_HED_TBA_SUCCESS });
    } catch (error) {
      console.error("resolveHedTba error", error);
      dispatch({ type: RESOLVE_HED_TBA_FAIL, error: error.message });
      throw error;
    }
  };
